import { FormikValues } from 'formik';
import { checkIfSerbianPhoneNumber, checkPhoneNumber, formatDate } from '@fnol/library/src/lib';
import { constValues, personType, legalPersonType, requestType, damageType, driverSelection } from './consts';

export const checkVehicleRegistration = (e: string) => {
    const isReg = /[(A-Z|ČčĆćŽžŠš|0-9|)]{3,15}$/i;
    return isReg.test(e);
};

export const checkVIN = (e: string) => {
    let isVIN: RegExp;

    if (e.toUpperCase().startsWith('PUS')) {
        isVIN = /^[PUS]+[0-9]{5,14}$/;
    } else {
        isVIN = /^((?!I|O|Q)[A-Z0-9]){17}$/;
    }
    return isVIN.test(e.toUpperCase());
};

export const checkYearNumber = (e: string) => {
    const isNumber = /^[0-9]{4}$/;
    return isNumber.test(e);
};

export const prepareMinutes = () => {
    const minutes = Array.from(Array(60).keys())
        .filter((minute) => minute % 5 === 0)
        .map((minute) => (minute < 10 ? '0' + minute : minute))
        .map(String);

    return minutes.map((minute) => {
        return {
            id: `minute-${minute}`,
            text: minute,
            value: minute,
        };
    });
};

export interface IVehicleType {
    id: number;
    naziv: string;
    text: string;
}

export const prepareVehicleTypesList = (vehicleTypes: IVehicleType[]) => {
    return vehicleTypes.map((vehicleType) => {
        return {
            id: vehicleType.id,
            text: vehicleType.naziv.charAt(0).toUpperCase() + vehicleType.naziv.slice(1),
            value: vehicleType.id,
        };
    });
};

export const isCountrySerbia = (countryId: string | number): boolean => {
    return countryId === constValues.countrySerbiaId.toString(10) || countryId === constValues.countrySerbiaId;
};

/**
 * Prepare leasing entities data received from API
 *
 * @param leasingEntities JSON list of leeasing entities
 * @returns Prepared list of leasing entities
 */
export const prepareLeasingEntities = (
    leasingEntities: {
        id: number;
        naziv: string;
        maticniBroj: string;
    }[],
) => {
    return leasingEntities.map((leasingEntity) => {
        return {
            id: leasingEntity.id,
            value: leasingEntity.id,
            text: leasingEntity.naziv,
            code: leasingEntity.maticniBroj,
        };
    });
};

/**
 * Prepare person roles data received from API
 *
 * @param personRoles JSON list of person roles
 * @returns Prepared list of person roles filtered by personType
 */
export const preparePersonRoles = (
    personRoles: {
        id: number;
        naziv: string;
        idTipLica: number;
    }[],
    personType: personType,
) => {
    return personRoles
        .filter((filter) => {
            return filter.idTipLica === personType;
        })
        .map((personRole) => {
            return {
                id: personRole.id,
                value: personRole.id,
                text: personRole.naziv,
            };
        });
};

/**
 * Prepare person relations data received from API
 *
 * @param personRelations JSON list of person relations
 * @returns Prepared list of person relations filtered by personType, idLica2, tipLica1, tipLica2
 */
export const preparePersonRelationsList = (
    personRelations: { idSvojstva: number; svojstvo: string; idLica2: number; tipLica1: string; tipLica2: string }[],
    personType: number,
    legalPersonTypeSubmitter: legalPersonType,
    secondPersonLegalType: legalPersonType,
) => {
    return personRelations
        .filter((personRelation) => {
            return personRelation.idLica2 === personType;
        })
        .filter((personRelation) => {
            if (legalPersonTypeSubmitter === '1') {
                return personRelation.tipLica1 === 'FL';
            } else if (legalPersonTypeSubmitter === '2') {
                return personRelation.tipLica1 === 'PL';
            }
        })
        .filter((personRelation) => {
            if (secondPersonLegalType === '1') {
                return personRelation.tipLica2 === 'FL';
            } else if (secondPersonLegalType === '2') {
                return personRelation.tipLica2 === 'PL';
            }
        })
        .map((personRelation) => {
            return {
                id: personRelation.idSvojstva,
                value: personRelation.idSvojstva,
                text: personRelation.svojstvo,
            };
        });
};

/**
 * Prepare industrial classification data received from API
 *
 * @param industrialClassifications JSON list of industrial classifications
 * @returns Prepared list of industrial classifications
 */
export const prepareIndustrialClassifications = (
    industrialClassifications: {
        sifraDel: string;
        nazivDel: string;
        sifraSektora: string;
    }[],
) => {
    return industrialClassifications.map((industrialClassification) => {
        return {
            id: industrialClassification.sifraDel,
            value: industrialClassification.sifraDel,
            text: industrialClassification.nazivDel,
        };
    });
};

export const removeEmptyProperties = (array: any) => {
    return array.filter((obj: any) => {
        for (const value in obj) {
            if (obj[value] === ' ') return false;
        }
        return true;
    });
};

export function showPersonsWithPhoneNumber(array: { phoneNumber: string }[]): { phoneNumber: string }[] {
    return array.filter((person) => {
        return checkIfSerbianPhoneNumber(person.phoneNumber) && checkPhoneNumber(person.phoneNumber);
    });
}

/**
 * Prepare Formik data for submit
 *
 * @param values - object containing Formik state
 * @returns dataForSubmit - object with mapped data for submit
 */
type GenericObject = { [key: string]: any };
export const prepareDataForSubmit = (values: FormikValues) => {
    const dataForSubmit: GenericObject = {};
    dataForSubmit.brojPolise = values.policyNumber;
    dataForSubmit.datumNastanka = formatDate(values.dateOfOccurance);
    dataForSubmit.vremeNastanka = `${values.hourOfOccurance}:${values.minuteOfOccurance}`;
    dataForSubmit.vrstaZahteva = values.requestType;
    dataForSubmit.podnosilacVrstaLica = values.submitterType;
    if (values.submitterType === legalPersonType.individual) {
        dataForSubmit.podnosilacFL = {
            ime: values.submitterIndividualName,
            prezime: values.submitterIndividualLastName,
            drzavaId: values.submitterIndividualCountry,
            adresa: values.submitterIndividualStreet,
            jmbg: values.submitterIndividualUCN,
            mobilniTelefon: values.submitterIndividualPhone,
            email: values.submitterIndividualEmail,
            nematerijalnePosledice: values.submitterIndividualReportingImmaterialDamage === 'true',
            nematerijalnePoslediceNapomena: values.submitterIndividualImmaterialDamageDescription,
            svojstvoUcesnikaId: values.submitterIndividualPersonRoleId,
        };
        if (
            values.submitterIndividualCountry !== constValues.countrySerbiaId.toString(10) &&
            values.submitterIndividualCountry !== constValues.countrySerbiaId
        ) {
            dataForSubmit.podnosilacFL.mesto = values.submitterIndividualCity;
        } else {
            dataForSubmit.podnosilacFL.mestoId = values.submitterIndividualCityInSerbia.id;
            dataForSubmit.podnosilacFL.mesto = values.submitterIndividualCityInSerbia.label;
        }
    }
    if (
        values.submitterType === legalPersonType.legal &&
        values.submitterIndustrialClassificationType !== constValues.industrialClassificationTypeLeasing
    ) {
        dataForSubmit.podnosilacPL = {
            sifraDelatnosti:
                values.submitterIndustrialClassificationType === '0'
                    ? ''
                    : values.submitterIndustrialClassificationType,
            naziv: values.submitterLegalEntityName,
            drzavaId: values.submitterLegalEntityCountry,
            adresa: values.submitterLegalEntityStreet,
            maticniBroj: values.submitterLegalEntityID,
            pib: values.submitterLegalEntityVAT,
            email: values.submitterLegalEntityEmail,
            telefon: values.submitterLegalEntityPhone,
            kontaktIme: values.submitterLegalEntityContactName,
            kontaktPrezime: values.submitterLegalEntityContactLastName,
            kontaktMobilniTelefon: values.submitterLegalEntityContactPhone,
            kontaktEmail: values.submitterLegalEntityContactEmail,
        };
        if (
            values.submitterLegalEntityCountry !== constValues.countrySerbiaId.toString(10) &&
            values.submitterLegalEntityCountry !== constValues.countrySerbiaId
        ) {
            dataForSubmit.podnosilacPL.mesto = values.submitterLegalEntityCity;
        } else {
            dataForSubmit.podnosilacPL.mestoId = values.submitterLegalEntityCityInSerbia.id;
            dataForSubmit.podnosilacPL.mesto = values.submitterLegalEntityCityInSerbia.label;
        }
    }
    if (
        values.submitterType === legalPersonType.legal &&
        values.submitterIndustrialClassificationType === constValues.industrialClassificationTypeLeasing
    ) {
        dataForSubmit.podnosilacVrstaLica = 3;
        dataForSubmit.podnosilacLizing = {
            sifraDelatnosti:
                values.submitterIndustrialClassificationType === '0'
                    ? ''
                    : values.submitterIndustrialClassificationType,
            lizingId: values.submitterLeasingEntityID,
            kontaktIme: values.submitterLegalEntityContactName,
            kontaktPrezime: values.submitterLegalEntityContactLastName,
            kontaktMobilniTelefon: values.submitterLegalEntityContactPhone,
            kontaktEmail: values.submitterLegalEntityContactEmail,
        };
    }
    dataForSubmit.mestoNastankaId = values.cityInSerbiaClaim.id;
    dataForSubmit.ulicaRelacija = values.streetClaim;
    dataForSubmit.evropskiIzvestaj = values.euReport === 'true';
    dataForSubmit.policijaPrijava = values.reportedToPolice === 'true';
    dataForSubmit.policijaIzlazak = values.policeOnScene === 'true';
    dataForSubmit.policijaUvidjaj = values.policeInspection === 'true';
    dataForSubmit.opisDogadjaja = values.howDidDamageOccur;
    if (
        (values.requestType === requestType.MaterialDamage || values.requestType === requestType.Both) &&
        values.damageType === damageType.vehicle
    ) {
        dataForSubmit.vrstaMaterijalneStete = 1;
        const vehicleData: GenericObject = {};
        vehicleData.vrstaVozilaId = values.vehicleType;
        vehicleData.brojSasije = values.chassisNumber;
        vehicleData.markaVozila = values.vehicleBrand;
        vehicleData.tipVozila = values.vehicleModel;
        vehicleData.registarskaOznaka = values.vehicleReg;
        vehicleData.godinaProizvodnje = values.vehicleProductionYear;
        vehicleData.vlasnikPodnosilac = values.isSubmitterOwnerOfVehicle === 'true';
        if (!vehicleData.vlasnikPodnosilac) {
            vehicleData.vlasnikVrstaLica = values.ownerType;
            if (values.ownerType === legalPersonType.individual) {
                vehicleData.vlasnikFL = {
                    ime: values.ownerIndividualName,
                    prezime: values.ownerIndividualLastName,
                    drzavaId: values.ownerIndividualCountry,
                    adresa: values.ownerIndividualStreet,
                    jmbg: values.ownerIndividualUCN,
                    mobilniTelefon: values.ownerIndividualPhone,
                    email: values.ownerIndividualEmail,
                    svojstvoLicaId: values.ownerIndividualSubmitterRelationId,
                    nematerijalnePosledice: values.ownerIndividualReportingImmaterialDamage === 'true',
                    nematerijalnePoslediceNapomena: values.ownerIndividualImmaterialDamageDescription,
                    svojstvoUcesnikaId: values.ownerIndividualPersonRoleId,
                };
                if (
                    values.ownerIndividualCountry !== constValues.countrySerbiaId.toString(10) &&
                    values.ownerIndividualCountry !== constValues.countrySerbiaId
                ) {
                    vehicleData.vlasnikFL.mesto = values.ownerIndividualCity;
                } else {
                    vehicleData.vlasnikFL.mestoId = values.ownerIndividualCityInSerbia.id;
                    vehicleData.vlasnikFL.mesto = values.ownerIndividualCityInSerbia.label;
                }
            }
            if (
                values.ownerType === legalPersonType.legal &&
                values.ownerIndustrialClassificationType !== constValues.industrialClassificationTypeLeasing
            ) {
                vehicleData.vlasnikPL = {
                    sifraDelatnosti:
                        values.ownerIndustrialClassificationType === '0'
                            ? ''
                            : values.ownerIndustrialClassificationType,
                    naziv: values.ownerLegalEntityName,
                    drzavaId: values.ownerLegalEntityCountry,
                    adresa: values.ownerLegalEntityStreet,
                    maticniBroj: values.ownerLegalEntityID,
                    pib: values.ownerLegalEntityVAT,
                    email: values.ownerLegalEntityEmail,
                    telefon: values.ownerLegalEntityPhone,
                    svojstvoLicaId: values.ownerLegalEntityPersonRoleId,
                };
                if (
                    values.ownerLegalEntityCountry !== constValues.countrySerbiaId.toString(10) &&
                    values.ownerLegalEntityCountry !== constValues.countrySerbiaId
                ) {
                    vehicleData.vlasnikPL.mesto = values.ownerLegalEntityCity;
                } else {
                    vehicleData.vlasnikPL.mestoId = values.ownerLegalEntityCityInSerbia.id;
                    vehicleData.vlasnikPL.mesto = values.ownerLegalEntityCityInSerbia.label;
                }
            }
            if (
                values.ownerType === legalPersonType.legal &&
                values.ownerIndustrialClassificationType === constValues.industrialClassificationTypeLeasing
            ) {
                vehicleData.vlasnikVrstaLica = 3;
                vehicleData.vlasnikLizing = {
                    sifraDelatnosti:
                        values.ownerIndustrialClassificationType === '0'
                            ? ''
                            : values.ownerIndustrialClassificationType,
                    lizingId: values.ownerLeasingEntityID,
                    svojstvoLicaId: values.ownerLegalEntityPersonRoleId,
                };
            }
        }
        vehicleData.korisnikUgovor = values.howIsVehicleUsed;
        vehicleData.korisnikPodnosilac = values.submitterUserByVehicleRegistration !== 'false';
        if (!vehicleData.korisnikPodnosilac) {
            vehicleData.korisnikVrstaLica = values.userType;
            if (values.userType === legalPersonType.individual) {
                vehicleData.korisnikFL = {
                    ime: values.userIndividualName,
                    prezime: values.userIndividualLastName,
                    drzavaId: values.userIndividualCountry,
                    adresa: values.userIndividualStreet,
                    jmbg: values.userIndividualUCN,
                    mobilniTelefon: values.userIndividualPhone,
                    email: values.userIndividualEmail,
                    svojstvoLicaId: values.userIndividualSubmitterRelationId,
                    nematerijalnePosledice: values.userIndividualReportingImmaterialDamage === 'true',
                    nematerijalnePoslediceNapomena: values.userIndividualImmaterialDamageDescription,
                    svojstvoUcesnikaId: values.userIndividualPersonRoleId,
                };
                if (
                    values.userIndividualCountry !== constValues.countrySerbiaId.toString(10) &&
                    values.userIndividualCountry !== constValues.countrySerbiaId
                ) {
                    vehicleData.korisnikFL.mesto = values.userIndividualCity;
                } else {
                    vehicleData.korisnikFL.mestoId = values.userIndividualCityInSerbia.id;
                    vehicleData.korisnikFL.mesto = values.userIndividualCityInSerbia.label;
                }
            }
            if (
                values.userType === legalPersonType.legal &&
                values.userIndustrialClassificationType !== constValues.industrialClassificationTypeLeasing
            ) {
                vehicleData.korisnikPL = {
                    sifraDelatnosti:
                        values.userIndustrialClassificationType === '0' ? '' : values.userIndustrialClassificationType,
                    naziv: values.userLegalEntityName,
                    drzavaId: values.userLegalEntityCountry,
                    adresa: values.userLegalEntityStreet,
                    maticniBroj: values.userLegalEntityID,
                    pib: values.userLegalEntityVAT,
                    email: values.userLegalEntityEmail,
                    telefon: values.userLegalEntityPhone,
                    svojstvoLicaId: values.userLegalEntityPersonRoleId,
                };
                if (
                    values.userLegalEntityCountry !== constValues.countrySerbiaId.toString(10) &&
                    values.userLegalEntityCountry !== constValues.countrySerbiaId
                ) {
                    vehicleData.korisnikPL.mesto = values.userLegalEntityCity;
                } else {
                    vehicleData.korisnikPL.mestoId = values.userLegalEntityCityInSerbia.id;
                    vehicleData.korisnikPL.mesto = values.userLegalEntityCityInSerbia.label;
                }
            }
            if (
                values.userType === legalPersonType.legal &&
                values.userIndustrialClassificationType === constValues.industrialClassificationTypeLeasing
            ) {
                vehicleData.korisnikVrstaLica = 3;
                vehicleData.korisnikLizing = {
                    sifraDelatnosti:
                        values.userIndustrialClassificationType === '0' ? '' : values.userIndustrialClassificationType,
                    lizingId: values.userLeasingEntityID,
                    svojstvoLicaId: values.userLegalEntityPersonRoleId,
                };
            }
        }
        vehicleData.parkiranoVozilo = values.isVehicleParked === 'true';
        if (!vehicleData.parkiranoVozilo) {
            if (values.pickDriver === driverSelection.other) {
                vehicleData.vrstaVozaca = 0;
                vehicleData.vozacFL = {
                    ime: values.driverIndividualName,
                    prezime: values.driverIndividualLastName,
                    drzavaId: values.driverIndividualCountry,
                    adresa: values.driverIndividualStreet,
                    jmbg: values.driverIndividualUCN,
                    mobilniTelefon: values.driverIndividualPhone,
                    email: values.driverIndividualEmail,
                    svojstvoLicaId: values.driverIndividualSubmitterRelationId,
                    nematerijalnePosledice: values.driverIndividualReportingImmaterialDamage === 'true',
                    nematerijalnePoslediceNapomena: values.driverIndividualImmaterialDamageDescription,
                    svojstvoUcesnikaId: values.driverIndividualPersonRoleId,
                };
                if (
                    values.driverIndividualCountry !== constValues.countrySerbiaId.toString(10) &&
                    values.driverIndividualCountry !== constValues.countrySerbiaId
                ) {
                    vehicleData.vozacFL.mesto = values.driverIndividualCity;
                } else {
                    vehicleData.vozacFL.mestoId = values.driverIndividualCityInSerbia.id;
                    vehicleData.vozacFL.mesto = values.driverIndividualCityInSerbia.label;
                }
            } else {
                vehicleData.vrstaVozaca = values.pickDriver ? values.pickDriver : driverSelection.submitter;
            }
        }
        vehicleData.voziloPokretno = values.isVehicleDrivable === 'true';
        if (!vehicleData.voziloPokretno) {
            vehicleData.voziloLokacija = values.vehicleLocation;
        }
        vehicleData.ostecenja = values.vehicleDamageDescription;
        vehicleData.ostecenjaDrugo = values.vehicleOtherDamages;
        vehicleData.servisVozila = values.vehicleService;
        vehicleData.placanje = values.payment;
        vehicleData.samostalnoSnimanjeStete = values.smsLink === 'true';
        if (vehicleData.samostalnoSnimanjeStete) {
            switch (values.sendLink) {
                case 1:
                    vehicleData.samostalnoSnimanjeSteteTelefon = values.submitterIndividualPhone;
                    break;
                case 2:
                    vehicleData.samostalnoSnimanjeSteteTelefon = values.ownerIndividualPhone;
                    break;
                case 3:
                    vehicleData.samostalnoSnimanjeSteteTelefon = values.userIndividualPhone;
                    break;
                case 4:
                    vehicleData.samostalnoSnimanjeSteteTelefon = values.submitterLegalEntityContactPhone;
                    break;
                case 5:
                    vehicleData.samostalnoSnimanjeSteteTelefon = values.driverIndividualPhone;
                    break;
                default:
                    vehicleData.samostalnoSnimanjeSteteTelefon = values.vehicleDamageMobile;
            }
        }
        dataForSubmit.ostecenoVozilo = vehicleData;
    }
    if (
        (values.requestType === requestType.MaterialDamage || values.requestType === requestType.Both) &&
        values.damageType === damageType.other
    ) {
        dataForSubmit.vrstaMaterijalneStete = 2;
        const objectData: GenericObject = {};
        objectData.vrstaObjekta = values.objectType;
        objectData.opisObjekta = values.objectDescription;
        objectData.starost = values.objectConstructionYear;
        objectData.vlasnikPodnosilac = values.objectOwnerSubmitter === 'true';
        if (!objectData.vlasnikPodnosilac) {
            objectData.vlasnikVrstaLica = values.ownerBuildingType;
            if (values.ownerBuildingType === legalPersonType.individual) {
                objectData.vlasnikFL = {
                    ime: values.ownerBuildingIndividualName,
                    prezime: values.ownerBuildingIndividualLastName,
                    drzavaId: values.ownerBuildingIndividualCountry,
                    adresa: values.ownerBuildingIndividualStreet,
                    jmbg: values.ownerBuildingIndividualUCN,
                    mobilniTelefon: values.ownerBuildingIndividualPhone,
                    email: values.ownerBuildingIndividualEmail,
                    svojstvoLicaId: values.ownerBuildingIndividualSubmitterRelationId,
                    nematerijalnePosledice: values.ownerBuildingIndividualReportingImmaterialDamage === 'true',
                    nematerijalnePoslediceNapomena: values.ownerBuildingIndividualImmaterialDamageDescription,
                    svojstvoUcesnikaId: values.ownerBuildingIndividualPersonRoleId,
                };
                if (
                    values.ownerBuildingIndividualCountry !== constValues.countrySerbiaId.toString(10) &&
                    values.ownerBuildingIndividualCountry !== constValues.countrySerbiaId
                ) {
                    objectData.vlasnikFL.mesto = values.ownerBuildingIndividualCity;
                } else {
                    objectData.vlasnikFL.mestoId = values.ownerBuildingIndividualCityInSerbia.id;
                    objectData.vlasnikFL.mesto = values.ownerBuildingIndividualCityInSerbia.label;
                }
            }
            if (
                values.ownerBuildingType === legalPersonType.legal &&
                values.ownerBuildingIndustrialClassificationType !== constValues.industrialClassificationTypeLeasing
            ) {
                objectData.vlasnikPL = {
                    sifraDelatnosti:
                        values.ownerBuildingIndustrialClassificationType === '0'
                            ? ''
                            : values.ownerBuildingIndustrialClassificationType,
                    naziv: values.ownerBuildingLegalEntityName,
                    drzavaId: values.ownerBuildingLegalEntityCountry,
                    adresa: values.ownerBuildingLegalEntityStreet,
                    maticniBroj: values.ownerBuildingLegalEntityID,
                    pib: values.ownerBuildingLegalEntityVAT,
                    email: values.ownerBuildingLegalEntityEmail,
                    telefon: values.ownerBuildingLegalEntityPhone,
                    svojstvoLicaId: values.ownerBuildingLegalEntityPersonRoleId,
                };
                if (
                    values.ownerBuildingLegalEntityCountry !== constValues.countrySerbiaId.toString(10) &&
                    values.ownerBuildingLegalEntityCountry !== constValues.countrySerbiaId
                ) {
                    objectData.vlasnikPL.mesto = values.ownerBuildingLegalEntityCity;
                } else {
                    objectData.vlasnikPL.mestoId = values.ownerBuildingLegalEntityCityInSerbia.id;
                    objectData.vlasnikPL.mesto = values.ownerBuildingLegalEntityCityInSerbia.label;
                }
            }
            if (
                values.ownerBuildingType === legalPersonType.legal &&
                values.ownerBuildingIndustrialClassificationType === constValues.industrialClassificationTypeLeasing
            ) {
                objectData.vlasnikVrstaLica = 3;
                objectData.vlasnikLizing = {
                    sifraDelatnosti:
                        values.ownerBuildingIndustrialClassificationType === '0'
                            ? ''
                            : values.ownerBuildingIndustrialClassificationType,
                    lizingId: values.ownerBuildingLeasingEntityID,
                    svojstvoLicaId: values.ownerBuildingLegalEntityPersonRoleId,
                };
            }
        }
        objectData.objekatLokacija = values.objectLocation;
        objectData.ostecenja = values.objectDamageDescription;
        objectData.ostecenjaNapomena = values.moreObjectDamagesDescription;
        dataForSubmit.ostecenObjekat = objectData;
    }
    if (values.requestType === requestType.ImmaterialDamage || values.requestType === requestType.Both) {
        const injuredPersons: GenericObject[] = [];
        const injuredIds = [1, 2, 3, 4, 5];
        injuredIds.map((injuredId) => {
            if (values[`injured${injuredId}IndividualName`]) {
                const injuredPerson: GenericObject = {
                    ime: values[`injured${injuredId}IndividualName`],
                    prezime: values[`injured${injuredId}IndividualLastName`],
                    drzavaId: values[`injured${injuredId}IndividualCountry`],
                    adresa: values[`injured${injuredId}IndividualStreet`],
                    jmbg: values[`injured${injuredId}IndividualUCN`],
                    mobilniTelefon: values[`injured${injuredId}IndividualPhone`],
                    email: values[`injured${injuredId}IndividualEmail`],
                    svojstvoLicaId: values[`injured${injuredId}IndividualSubmitterRelationId`],
                    svojstvoUcesnikaId: values[`injured${injuredId}IndividualPersonRoleId`],
                    nematerijalnePoslediceNapomena: values[`injured${injuredId}IndividualImmaterialDamageDescription`],
                };
                if (
                    values[`injured${injuredId}IndividualCountry`] !== constValues.countrySerbiaId.toString(10) &&
                    values[`injured${injuredId}IndividualCountry`] !== constValues.countrySerbiaId
                ) {
                    injuredPerson.mesto = values[`injured${injuredId}IndividualCity`];
                } else {
                    injuredPerson.mestoId = values[`injured${injuredId}IndividualCityInSerbia`].id;
                    injuredPerson.mesto = values[`injured${injuredId}IndividualCityInSerbia`].label;
                }
                injuredPersons.push(injuredPerson);
            }
        });
        if (injuredPersons.length > 0) {
            dataForSubmit.povredjenaLica = injuredPersons;
        }
    }
    return dataForSubmit;
};
